import React from "react";
import { graphql, prefetchPathname } from "gatsby";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import { Element } from 'react-scroll';

import toHTML from "../helpers/toHtml";
import Layout from "../components/Layout";
import TiltedImg from "../components/TiltedImg";
import Services from "../components/Services";
import Footer from "../components/Footer";
import colors from "../styles/colors";
import Wrapper from "../components/styled/wrapper";
import Employees from "../components/Employees";
import Menu from "../components/Menu";

import Logo from '../img/logo.png';
import Cases from "../components/Cases";
import Policies from "../components/Policies";

export const IndexPageTemplate = ({
  title,
  tagline,
  image,
  excerpt,
  about,
  services,
  cases,
  employees,
  casesTitle,
  casesTagline,
  employeeTitle,
  policies,
  footer,
}) => {
  return (
    <main>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={about} />
        <meta property="og:title" content={title} />
      </Helmet>
      {process.env.FEATURE_MENU === "true" && (
        <Menu links={[
          { title: 'Om oss', section: 'about' },
          { title: 'Tjänster', section: 'services' },
          { title: casesTitle, section: 'cases' },
          { title: employeeTitle, section: 'contacts' }
        ]} />
      )}

      {process.env.FEATURE_HEADER === "true" && (
        <Element name="about">
          <Hero>
            <div className="text">
              <LogoType src={Logo} alt="Torslanda Entrepenad Logo" />
              {title && <h1 className="h3">{title}</h1>}
              {tagline && <p className="preamble">{tagline}</p>}
            </div>
            <TiltedImg image={image} />
          </Hero>
        </Element>
      )}

      {process.env.FEATURE_USP === "true" && (
        <TextSection >
          <Wrapper>
            <Split>
              <Excerpt>“{excerpt}”</Excerpt>

              <TextWrapper
                dangerouslySetInnerHTML={{ __html: toHTML(about) }}
              ></TextWrapper>
            </Split>
          </Wrapper>
        </TextSection>
      )}

      {process.env.FEATURE_SERVICES === "true" && (
        <Element name="services">
          <Services services={services} />
        </Element>
      )}
      {process.env.FEATURE_CASES === "true" && (
        <Element name="cases">
          <Cases title={casesTitle} tagline={casesTagline} cases={cases} />
        </Element>
      )}

      {process.env.FEATURE_EMPLOYEES === "true" && (
        <Element name="contacts">
          <Employees title={employeeTitle} employees={employees} />
        </Element>
      )}

      {process.env.FEATURE_POLICIES === "true" && <Policies policies={policies} />}
      {process.env.FEATURE_FOOTER === "true" && <Footer footer={footer} />}
    </main>
  );
};

const IndexPage = ({ data }) => {
  const { frontmatter } = data.markdownRemark;
  const employees = data.employees.edges.map(edge => edge.node);
  const cases = data.cases.edges.map(edge => edge.node);

  console.log(
    'page data', frontmatter,
    'employees', employees,
    'cases', cases
  );

  return (
    <Layout>
      <IndexPageTemplate
        title={frontmatter.title}
        tagline={frontmatter.tagline}
        image={frontmatter.image}
        excerpt={frontmatter.excerpt}
        about={frontmatter.about}
        services={frontmatter.services}
        cases={cases}
        casesTitle={frontmatter.casesTitle}
        casesTagline={frontmatter.casesTagline}
        employeeTitle={frontmatter.employeeTitle}
        employees={employees}
        policies={frontmatter.policies}
        footer={frontmatter.footer}
      />
    </Layout>
  );
};

export default IndexPage;

export const pageQuery = graphql`
  query IndexPageTemplate {
    markdownRemark(frontmatter: { templateKey: { eq: "index-page" } }) {
      frontmatter {
        title
        tagline
        image {
          childImageSharp {
            fluid(maxWidth: 2048, quality: 100) {
              ...GatsbyImageSharpFluid
            }
          }
        }
        excerpt
        about
        policies {
          col1
          col2
          col3
        }
        footer {
          col1
          col2
          col3
        }
        casesTitle
        casesTagline
        employeeTitle
        services {
          title
          description
          image {
            childImageSharp {
              fluid(maxWidth: 2048, quality: 100) {
                ...GatsbyImageSharpFluid
              }
            }
          }
        }
      }
      timeToRead
    }
    cases: allMarkdownRemark(
      filter: {frontmatter: {templateKey: {eq: "case"}}}
    ){
      edges {
        node {
          id
          html
          fields {
            slug
          }
          frontmatter {
            title
            startDate
            endDate
            description
            preamble
            galleryImages {
              childImageSharp {
                fluid(maxWidth: 700, quality: 100) {
                  ...GatsbyImageSharpFluid
                }
              }
            }
          }
        }
      }
    }
    employees: allMarkdownRemark(
      filter: { frontmatter: { templateKey: { eq: "employee" } } }
    ) {
      edges {
        node {

          id
          html
          fields {
            slug
          }
          frontmatter {
            name
            email
            phone
            position
            department
            featured
            image {
              childImageSharp {
                fixed(width: 80 height: 80 quality: 100) {
                  ...GatsbyImageSharpFixed
                }
              }
            }
          }
        }
      }
    }
  }
`;

const LogoType = styled.img`
  width: 200px;
  margin-bottom: 3rem;
  @media (min-width: 700px) {
    width: 370px;
  }
`

const TextSection = styled.section`
  padding: 6rem 0;
  background-color: ${colors.offWhite07};
`;

const Excerpt = styled.blockquote`
  font-size: 1.953rem;
  max-width: 90%;
  width: 35rem;
  font-weight: 800;
  font-style: italic;
  text-align: center;

  @media (min-width: 700px) {
    width: 25rem;
    text-align: right;
  }
`;

const Split = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  flex-direction: column;

  @media (min-width: 700px) {
    flex-direction: row;
  }
`;

const TextWrapper = styled.div`
  max-width: 95%;

  @media (min-width: 700px) {
    flex-basis: 30rem;
  }
`;

const Hero = styled.header`
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  overflow: hidden;
  height: 50vh;

  @media (min-width: 700px) {
    grid-template-columns: 1fr 1fr;
    height: 80vh;
  }

  > .image {
    grid-row-start: 1;
    grid-column-start: 1;
    grid-column-end: 1;
    filter: blur(0.8rem);
    @media (min-width: 700px) {
      filter: blur(0);
      grid-row-start: auto;
      grid-column-start: auto;
      grid-column-end: auto;
    }
  }

  > .text {
    grid-row-start: 1;
    grid-column-start: 1;
    grid-column-end: span 3;
    z-index: 1;
    background: rgba(255, 255, 255, 0.6);
    width: 100%;

    padding: 13% 5% 5% 5%;
    justify-self: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;

    @media (min-width: 700px) {
      grid-row-start: auto;
      grid-column-start: auto;
      grid-column-end: auto;
    }

    > * {
      max-width: 50rem;
    }

    > h1 {
      max-width: 550px;
      margin-top: 0;
      margin-bottom: 0;
    }
  }
`;
